import { useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import ForecastAccountsPopover, { type AccountRow } from "./ForecastAccountsPopover";

export interface ForecastCustomer {
  name: string;
  pic_name: string | null;
}

export type ForecastCustomers = Record<string, ForecastCustomer>;
// One product's forecast cells: month ISO => [customer_id, units] pairs.
export type ForecastMonths = Record<string, [string, number][]>;

// Server omits the prop (null) unless the channel plans wholesale-style
// (manual) AND the viewer may see wholesale forecasts — render nothing when
// it's absent.
export interface WholesaleForecastData {
  customers: ForecastCustomers;
  products: Record<string, ForecastMonths>;
}

interface Props {
  monthIso: string;
  months: ForecastMonths;
  customers: ForecastCustomers;
  productExternalId: string;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function monthLabel(iso: string): string {
  const [y, m] = iso.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1] ?? iso} ${y}`;
}

function formatQty(n: number): string {
  return n.toLocaleString("en-US");
}

export default function ForecastAccountsCell({ monthIso, months, customers, productExternalId }: Props) {
  const pairs = months[monthIso];

  // Every account with a forecast row for this product in any month — quiet
  // accounts stay visible in the popover as "—" for this month.
  const accounts: AccountRow[] = useMemo(() => {
    const unitsById = new Map(pairs ?? []);
    const ids = new Set<string>();
    Object.values(months).forEach((list) => list.forEach(([id]) => ids.add(id)));
    return [...ids]
      .map((id) => ({
        id,
        name: customers[id]?.name ?? "(unknown)",
        picName: customers[id]?.pic_name ?? null,
        units: unitsById.get(id) ?? 0,
      }))
      .sort((a, b) => b.units - a.units || a.name.localeCompare(b.name));
  }, [months, customers, pairs]);

  if (!pairs || pairs.length === 0) return null;

  const total = pairs.reduce((sum, [, units]) => sum + units, 0);
  const contributing = pairs.filter(([, units]) => units > 0).length;

  return (
    <Popover>
      <PopoverTrigger
        title={`${formatQty(total)} forecast units from ${contributing} accounts`}
        style={{ color: "var(--muted-foreground)" }}
        className="block w-full text-right text-[10px] tabular-nums px-1.5 mt-0.5 cursor-pointer hover:underline decoration-dotted underline-offset-2"
      >
        {formatQty(total)} · {contributing} acct
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm tabular-nums">
        <ForecastAccountsPopover
          monthLabel={monthLabel(monthIso)}
          total={total}
          contributing={contributing}
          accounts={accounts}
          productExternalId={productExternalId}
        />
      </PopoverContent>
    </Popover>
  );
}
